import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Logo } from '@/components/feature/Logo';

export default function ForgotPasswordPage() {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await resetPassword(email);
    setLoading(false);
    if (result.error) {
      // Translate Supabase's rate-limit error into something a real user can
      // act on. Supabase free tier caps reset emails at ~3-4/hr per address.
      const msg = result.error.toLowerCase();
      if (msg.includes('rate limit') || msg.includes('too many')) {
        setError(
          "We've sent too many reset emails to this address recently. " +
          'Please wait a few minutes and try again, or check your spam folder ' +
          'for an earlier email.',
        );
      } else {
        setError(result.error);
      }
    } else {
      setSent(true);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-6">
            <i className="ri-mail-check-line text-emerald-600 text-2xl" />
          </div>
          <h2 className="font-display font-bold text-dark text-xl mb-3">Reset link sent</h2>
          <p className="text-dark-muted text-sm mb-6">
            Check your inbox at <span className="font-medium text-dark">{email}</span> for instructions to reset your password.
          </p>
          <a
            href="/auth/sign-in"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-all cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            Back to Sign In
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <a href="/" className="inline-flex items-center mb-4">
            <Logo height={73} />
          </a>
          <p className="text-dark-muted text-sm">Reset your password</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl border border-surface-dark/40 p-8 shadow-sm">
          {error && (
            <div className="mb-4 flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 text-red-700 text-sm font-medium border border-red-200">
              <i className="ri-error-warning-line" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-dark mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="w-full px-4 py-3 rounded-xl border border-surface-dark/60 bg-white text-dark text-sm placeholder:text-dark-muted/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-all cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
              ) : (
                <>
                  <i className="ri-send-plane-line" />
                  Send Reset Link
                </>
              )
              }
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-dark-muted mt-6">
          Remember your password?{' '}
          <a
            href="/auth/sign-in"
            className="font-semibold text-primary hover:text-primary-hover transition-colors"
          >
            Sign in
          </a>
        </p>
      </div>
    </div>
  );
}